import React, {useMemo, useCallback, useState} from 'react'
import PropTypes from 'prop-types'

// Components
import {
    Flex,
    AspectRatio,
    Box,
    Skeleton as ChakraSkeleton,
    Text,
    useMultiStyleConfig,
    VStack,
    Button,
    VisuallyHidden
} from '@chakra-ui/react'
import DynamicImage from '../dynamic-image'
import Swatch from '../../components/swatch-group/swatch'

// Hooks
import {useTheme} from '@chakra-ui/react'
import {useDerivedProduct} from '../../hooks'
import useMultiSite from '../../hooks/use-multi-site'

// Other
import {useColorVariation} from '../../hooks'
import {useProductDisplayImages} from '../../hooks/use-product-display-images'
import {PRODUCT_TILE_IMAGE_TYPE} from '../../constants'
import ProductAvailability from '../../pages/product-detail/partials/product-availability'
import {useMaterialVariation} from '../../hooks/use-variation-attributes'
import Chip from '../chip'
import {useHistory} from 'react-router-dom'
import {usePDPSearchParams} from '../../hooks/use-pdp-search-params'
import Checkbox from '../checkbox'
import {getMasterProductId, isProductOrderable} from '../../utils/product-utils'
import useSelectableSetProduct from '../../hooks/use-selectable-set-product'
import {trackEvent} from '../../tracking/analytics'
import ProductDisplayPrice from '../product-display-price'
import {useIntl} from 'react-intl'
import SkeletonText from '../skeleton-text'

// Component Skeleton
export const Skeleton = (props) => {
    const styles = useMultiStyleConfig('ProductTile')
    return (
        <Box data-testid="sf-product-tile-skeleton" {...props}>
            <Flex direction="column" gap={2}>
                <Box {...styles.imageWrapper}>
                    <AspectRatio ratio={1} {...styles.image}>
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
                <ChakraSkeleton width="80px" height="20px" />
                <ChakraSkeleton width={{base: '120px', md: '220px'}} height="12px" />
            </Flex>
        </Box>
    )
}

const SwitchableImage = ({image, show, loading}) => {
    return (
        <DynamicImage
            src={`${image.disBaseLink || image.link}[?sw={width}&q=60]`}
            widths={['70vw', '70vw', '40vw', '30vw']}
            imageProps={{
                alt: image.alt,
                ...(loading && {loading}),
                objectFit: 'contain',
                style: {
                    display: show === true ? 'block' : 'none'
                }
            }}
        />
    )
}

SwitchableImage.propTypes = {
    image: PropTypes.object,
    show: PropTypes.bool,
    loading: PropTypes.oneOf(['lazy', 'eager'])
}

const stopPropagation = (e) => {
    e?.stopPropagation?.()
}

/**
 * The ProductSetTile is a visual representation of a product set object:
 *  - The [Default] variation will show its images, name, selected material and color
 */
const ProductSetTile = ({
    product,
    variant,
    onClick: _onClick,
    onHoverChange,
    tileCheckboxEvent,
    loading
}) => {
    const theme = useTheme()
    const history = useHistory()
    const {site} = useMultiSite()
    const {formatMessage} = useIntl()
    const isOrderable = useMemo(() => isProductOrderable(product), [product])
    const [, productParams] = usePDPSearchParams(product.setProductId)
    const [isSelected, setSelected, isSelectedChanging] = useSelectableSetProduct(product)
    const styles = useMultiStyleConfig('ProductSetTile', {isOrderable, isSelected, variant})
    const {variationParams} = useDerivedProduct(product, true)
    const {name, productName} = product
    const onClick = useCallback(() => _onClick?.(product), [product, _onClick])
    const [hovered, setHovered] = useState(false)

    /* We use localized id and name because getProducts & getProduct returning different responses */
    const localizedProductName = name || productName

    // compute if different master/variant is loading to show appropriate loading state
    const [isVariantLoading, isMasterLoading] = useMemo(() => {
        const params = productParams
        const wantedPid = params.get('pid') || product?.id
        return [
            // variant is loading when different pid is selected than the loaded one
            wantedPid !== product?.id && wantedPid !== getMasterProductId(product),
            // master is loading when different pid is selected than the loaded one
            wantedPid !== product?.id &&
                wantedPid !== getMasterProductId(product) &&
                // and the pid is not in the variants list
                !product.variants?.find((variant) => variant.productId === wantedPid)
        ]
    }, [product, productParams])

    /**
     * Filter [imageGroups] by PRODUCT_TILE_IMAGE_TYPE and selected color variation.
     * Slice them because we need only first and second image
     */
    const images = useProductDisplayImages(product, PRODUCT_TILE_IMAGE_TYPE, variationParams, 2)

    /* Get extended variation attributes data with images etc. */
    // const colorVariations = useColorVariation(product)?.values
    const colorVariation = useColorVariation(product, true)
    const materialVariation = useMaterialVariation(product)

    const selectedColor = useMemo(
        () =>
            colorVariation?.values?.find(
                ({value}) => value === colorVariation.selectedValue.value
            ) || colorVariation?.values?.[0],
        [colorVariation]
    )
    const selectedMaterial = useMemo(
        () =>
            materialVariation?.values?.find(
                ({value}) => value === materialVariation.selectedValue.value
            ) || materialVariation?.values?.[0],
        [materialVariation]
    )

    const colors = useMemo(
        () =>
            [
                selectedColor,
                ...(colorVariation?.values?.filter(
                    ({value, orderable}) =>
                        orderable && value !== colorVariation.selectedValue.value
                ) || [])
            ].filter(Boolean),
        [selectedColor, colorVariation]
    )
    const displayColors = useMemo(() => colors.slice(0, colors.length > 4 ? 3 : 4), [colors])

    return (
        <Box
            role="group"
            aria-label={formatMessage({
                id: 'global.product',
                defaultMessage: 'Product'
            })}
            {...styles.container}
            data-testid="product-set-tile"
            onClick={onClick}
        >
            <Flex gap={2}>
                <Box>
                    {/* Image */}
                    {product && images && (
                        <Box
                            {...styles.imageWrapper}
                            onMouseEnter={() => setHovered(true)}
                            onMouseLeave={() => setHovered(false)}
                        >
                            <Box
                                position="absolute"
                                width="100%"
                                height="100%"
                                backgroundImage={theme.gradients.greyBackgroundOfProductImages}
                            />

                            {images[0] && (
                                <SwitchableImage
                                    image={images[0]}
                                    show={!hovered}
                                    loading={loading}
                                />
                            )}
                            {images[1] && (
                                <SwitchableImage
                                    image={images[1]}
                                    show={hovered}
                                    loading={loading || 'lazy'}
                                />
                            )}
                        </Box>
                    )}
                </Box>
                <Flex direction="column" gap={4} minWidth={0}>
                    {/* Title */}
                    <Text as="h3" textStyle="Regular Medium" minWidth={0}>
                        {localizedProductName}
                    </Text>

                    <Flex direction="column" gap={2}>
                        {/*Material*/}
                        {selectedMaterial &&
                            (isMasterLoading ? (
                                <ChakraSkeleton width="80px" height={37} />
                            ) : (
                                <Chip
                                    href={selectedMaterial.href}
                                    value={selectedMaterial.value}
                                    name={selectedMaterial.name}
                                    image={selectedMaterial.image}
                                    orderable={selectedMaterial.orderable}
                                    selected={true}
                                />
                            ))}

                        {/*Selected color*/}
                        {selectedColor ? (
                            <VStack {...styles.colorVariantContainer}>
                                {isMasterLoading ? (
                                    <SkeletonText
                                        width="80px"
                                        skeletonHeight="1.3em"
                                        textStyle="Regular Small"
                                        noOfLines={1}
                                    />
                                ) : (
                                    <Text textStyle="Regular Small" minWidth={0}>
                                        {selectedColor.name}
                                    </Text>
                                )}
                                {displayColors.length > 0 && (
                                    <Flex as="fieldset" {...styles.colorSwatches}>
                                        <Text as="legend" className="sr-only">
                                            {colorVariation.name}
                                        </Text>
                                        {isMasterLoading ? (
                                            <ChakraSkeleton width="80px" height="24px" />
                                        ) : (
                                            <>
                                                {displayColors.map((v) => (
                                                    <Swatch
                                                        key={v.value}
                                                        value={v.value}
                                                        name={v.name}
                                                        href={v.href}
                                                        disabled={!v.orderable}
                                                        selected={v.value === selectedColor.value}
                                                        size="sm"
                                                        htmlName={product.id}
                                                        // prevent triggering the parent onClick event from the radio button
                                                        containerHtmlProps={{
                                                            onClick: stopPropagation
                                                        }}
                                                        onChange={(_value, href) => {
                                                            if (!href) return
                                                            history.replace(href)
                                                        }}
                                                        onMouseEnter={() => {
                                                            onHoverChange?.({
                                                                currentId: product.id,
                                                                hoveredId: v.productId
                                                            })
                                                        }}
                                                        onMouseLeave={() => {
                                                            onHoverChange?.()
                                                        }}
                                                        onHoverChange={onHoverChange}
                                                    >
                                                        {selectedColor.image ? (
                                                            <Box
                                                                height="100%"
                                                                width="100%"
                                                                minWidth="24px"
                                                                minHeight="24px"
                                                                backgroundRepeat="no-repeat"
                                                                backgroundSize="cover"
                                                                backgroundColor={v.name.toLowerCase()}
                                                                backgroundImage={
                                                                    v.image
                                                                        ? `url("${v.image.link}")`
                                                                        : ''
                                                                }
                                                            />
                                                        ) : (
                                                            v.name
                                                        )}
                                                    </Swatch>
                                                ))}
                                                {displayColors.length < colors.length && (
                                                    <Button
                                                        variant="swatch"
                                                        onClick={(e) => {
                                                            stopPropagation(e)
                                                            onClick()
                                                        }}
                                                        aria-label={formatMessage(
                                                            {
                                                                id: 'product_set_tile.button.assistive_msg.more_colors',
                                                                defaultMessage:
                                                                    '{count} additional colors, opens modal'
                                                            },
                                                            {
                                                                count:
                                                                    colors.length -
                                                                    displayColors.length
                                                            }
                                                        )}
                                                    >
                                                        +
                                                        {Math.min(
                                                            99,
                                                            colors.length - displayColors.length
                                                        )}
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </Flex>
                                )}
                            </VStack>
                        ) : null}
                    </Flex>
                </Flex>
            </Flex>
            <Flex gap={2}>
                <Box
                    {...styles.checkboxContainer}
                    // prevent triggering the parent onClick event from the checkbox
                    onClick={stopPropagation}
                >
                    <Checkbox
                        isChecked={isSelected}
                        isDisabled={!isOrderable || isVariantLoading}
                        isIndeterminate={isVariantLoading}
                        onChange={() => {
                            trackEvent({
                                event: tileCheckboxEvent,
                                event_category: tileCheckboxEvent,
                                event_action: product.name
                            })
                            setSelected(!isSelected)
                        }}
                    >
                        <VisuallyHidden className="sr-only">
                            {formatMessage({
                                id: 'product_set_tile.button.assistive_msg.include_product',
                                defaultMessage: 'Include this product in the set'
                            })}
                        </VisuallyHidden>
                    </Checkbox>
                </Box>
                {!site?.isContentOnlySite && (
                    <ProductAvailability
                        loading={isVariantLoading}
                        flexShrink="0"
                        product={product}
                    />
                )}
                <Box {...styles.priceContainer}>
                    <ProductDisplayPrice
                        product={product}
                        loading={isVariantLoading || isSelectedChanging}
                        variant="productTile"
                    />
                </Box>
            </Flex>
        </Box>
    )
}

ProductSetTile.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.object,
    variant: PropTypes.oneOf(['default', 'selectable']),
    onClick: PropTypes.func,
    onHoverChange: PropTypes.func,
    tileCheckboxEvent: PropTypes.string,
    loading: PropTypes.oneOf(['lazy', 'eager'])
}

export default ProductSetTile
